import { useTranslation } from 'react-i18next';
import { formatPercent } from '../../Utils/decimalUtils';
import BilledImpactLine from './BilledImpactLine';
import ExcludedTieredPlansNote from './ExcludedTieredPlansNote';
import RoundProgressSummary from './RoundProgressSummary';
import type { RoundProgress } from './roundProgress';
import WorksheetSummaryCard from './WorksheetSummaryCard';
import { deltaClass, orEmpty, withSek } from './worksheetFormatters';

interface WorksheetSummaryProps {
    totalImpact: number;
    subscriptionImpact: number;
    transactionImpact: number;
    billedImpact: number;
    hasQuantity: boolean;
    currentMrr: number;
    newMrr: number;
    weightedIncrease: number;
    excludedTieredPlanCount: number;
    excludedTieredGrossRevenue: number;
    progress: RoundProgress;
}

const WorksheetSummary = ({
    totalImpact,
    subscriptionImpact,
    transactionImpact,
    billedImpact,
    hasQuantity,
    currentMrr,
    newMrr,
    weightedIncrease,
    excludedTieredPlanCount,
    excludedTieredGrossRevenue,
    progress,
}: WorksheetSummaryProps) => {
    const { t } = useTranslation();

    return (
        <div className="d-flex flex-wrap gap-16 mb-24">
            <WorksheetSummaryCard label={t('priceSimulation.totalImpact')} tooltip={t('priceSimulation.totalImpactTooltip')}>
                <span className={`heading-lg ${deltaClass(hasQuantity ? totalImpact : 0)}`}>{orEmpty(hasQuantity, withSek(totalImpact))}</span>
                <span className="body-xs text-secondary">{t('priceSimulation.subscriptionImpact', { amount: orEmpty(hasQuantity, withSek(subscriptionImpact)) })}</span>
                <span className="body-xs text-secondary">{t('priceSimulation.transactionImpact', { amount: orEmpty(hasQuantity, withSek(transactionImpact)) })}</span>
                <BilledImpactLine amount={billedImpact} hasQuantity={hasQuantity} className="align-self-start" textClassName="body-xs" />
                {excludedTieredPlanCount > 0 && <ExcludedTieredPlansNote grossRevenue={excludedTieredGrossRevenue} />}
            </WorksheetSummaryCard>
            <WorksheetSummaryCard label={t('priceSimulation.weightedIncrease')} tooltip={t('priceSimulation.weightedIncreaseTooltip')}>
                <span className="heading-lg text-primary">{orEmpty(hasQuantity, formatPercent(weightedIncrease))}</span>
            </WorksheetSummaryCard>
            <WorksheetSummaryCard label={t('priceSimulation.mrr')} tooltip={t('priceSimulation.mrrTooltip')}>
                <span className="heading-lg text-primary">{orEmpty(hasQuantity, withSek(newMrr))}</span>
                <span className="body-xs text-secondary">{t('priceSimulation.currentMrr', { amount: orEmpty(hasQuantity, withSek(currentMrr)) })}</span>
            </WorksheetSummaryCard>
            <WorksheetSummaryCard label={t('priceSimulation.leftToAdjust')}>
                <RoundProgressSummary progress={progress} />
            </WorksheetSummaryCard>
        </div>
    );
};

export default WorksheetSummary;
